import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChangeEvent, FC, useState } from 'react';
import { useUpdateTeamCaptains } from '@frx/frx-react-context';
import { Participant } from '@frx/frx-services';
import { Notification, PaginationList } from '../../../../../components';
import { Circle, Partner, StarWIthOutlineBg } from '../../../../../assets';
import { useEventId, useTeamId } from '../../../../../hooks';

export interface PromoteCoCaptainProps {
  teamMembers: Participant[];
  captainConsIds: string[];
  onClose?: () => void;
}

export interface TeamMemberRecordProps extends Partial<Participant> {
  selectedValue?: number;
  onClick: (consId: number) => void;
}

const TeamMemberRecord: FC<TeamMemberRecordProps> = ({
  selectedValue,
  name,
  onClick,
  consId,
}) => (
  <HStack justify="space-between">
    <Text fontSize="md" color="brand.additional.black" align="left">
      {name?.first} {name?.last}
    </Text>
    <IconButton
      icon={
        selectedValue === consId ? (
          <StarWIthOutlineBg />
        ) : (
          <Circle boxSize="24px" />
        )
      }
      aria-label="promote team select"
      variant="ghost"
      colorScheme="blue"
      onClick={() => onClick(consId as number)}
    />
  </HStack>
);

export const PromoteCoCaptain: FC<PromoteCoCaptainProps> = ({
  teamMembers,
  captainConsIds,
  onClose,
}) => {
  const [filteredMembers, setFilteredMembers] = useState<Participant[]>();
  const [selectedValue, setSelectedValue] = useState<number>();
  const eventId = useEventId();
  const teamId = useTeamId();
  const { mutate: promoteCoCaptain, error } = useUpdateTeamCaptains();

  const handleOnClose = () => {
    setFilteredMembers([]);
    setSelectedValue(undefined);
    if (onClose) {
      onClose();
    }
  };

  const handlePromoteCaptain = () => {
    const captainsBody = [...captainConsIds, selectedValue];
    promoteCoCaptain(
      {
        fr_id: eventId,
        captains: JSON.stringify(captainsBody).replaceAll(/[-[\]"\s]/g, ''),
        team_id: teamId,
      },
      {
        onSuccess: () => {
          handleOnClose();
        },
      }
    );
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.currentTarget.value;
    if (query) {
      setFilteredMembers(
        teamMembers
          .filter(
            (tm) =>
              tm.name?.first?.toLowerCase().includes(query.toLowerCase()) ||
              tm.name?.last?.toLowerCase().includes(query.toLowerCase())
          )
          ?.filter(({ aTeamCaptain }) => !aTeamCaptain)
      );
    } else {
      setFilteredMembers([]);
    }
  };

  return (
    <Stack px={2} spacing={4}>
      <VStack color="brand.primary.swatch1">
        <Icon as={Partner} boxSize={16} />
        <Heading fontSize="24px">Promote a co-captain</Heading>
      </VStack>
      <Text fontSize="md" color="brand.monochromatic.700" align="left">
        Find a team member to promote to co-captain
      </Text>
      <Stack spacing={1}>
        <Text fontSize="md" color="brand.monochromatic.700" align="left">
          Find a team member
        </Text>
        <Text fontSize="12px" color="brand.monochromatic.700" align="left">
          Results appear as you type
        </Text>
        <Input onChange={handleOnChange} />
      </Stack>
      {filteredMembers && filteredMembers.length > 0 && (
        <Stack spacing={2}>
          <Notification status="error" message={error?.message as string} />
          <Box p={4}>
            <PaginationList listItems={filteredMembers} listKey="consId">
              <TeamMemberRecord
                selectedValue={selectedValue}
                onClick={(consId: number) => setSelectedValue(consId)}
              />
            </PaginationList>
          </Box>
          <Button colorScheme="blue" onClick={handlePromoteCaptain}>
            Promote to co-captain
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
